import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import {
  portalAdminListUsers, portalAdminCreateUser, portalAdminUpdateUser,
  portalAdminResetPassword, portalAdminDeleteUser,
} from "../../lib/api";

const ROLES = ["LEA", "Medicaid Billing"];

function TempPasswordBanner({ email, password, onClose }) {
  if (!password) return null;
  return (
    <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm" data-testid="portal-temp-password">
      <p className="font-semibold text-emerald-800">Temporary password created</p>
      <p className="text-emerald-900 mt-1">
        We emailed these to <strong>{email}</strong>. You can also share them directly — they&apos;ll set their own password on first login.
      </p>
      <div className="mt-2 flex items-center gap-2">
        <code className="px-2 py-1 rounded bg-white border font-mono text-base tracking-wide">{password}</code>
        <Button size="sm" variant="outline" onClick={() => { navigator.clipboard?.writeText(password); toast.success("Copied"); }}>Copy</Button>
        <Button size="sm" variant="ghost" onClick={onClose}>Dismiss</Button>
      </div>
    </div>
  );
}

export default function DistrictPortalAccessDialog({ district, open, onOpenChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("LEA");
  const [creating, setCreating] = useState(false);
  const [tempPw, setTempPw] = useState(null); // {email, password}

  const load = useCallback(async () => {
    if (!district) return;
    setLoading(true);
    try {
      const res = await portalAdminListUsers(district);
      setUsers(res.data || []);
    } catch {
      toast.error("Failed to load portal logins");
    } finally { setLoading(false); }
  }, [district]);

  useEffect(() => { if (open) { load(); setTempPw(null); setEmail(""); setName(""); setRole("LEA"); } }, [open, load]);

  const create = async () => {
    if (!email.trim() || !name.trim()) { toast.error("Name and email are required"); return; }
    setCreating(true);
    try {
      const res = await portalAdminCreateUser({ email: email.trim(), name: name.trim(), role, district });
      setTempPw({ email: res.data.email, password: res.data.temp_password });
      setEmail(""); setName("");
      toast.success("Portal login created");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create login");
    } finally { setCreating(false); }
  };

  const toggleActive = async (u) => {
    try {
      await portalAdminUpdateUser(u.id, { active: !u.active });
      toast.success(u.active ? "Access disabled" : "Access enabled");
      load();
    } catch { toast.error("Failed to update"); }
  };

  const changeRole = async (u, newRole) => {
    try {
      await portalAdminUpdateUser(u.id, { role: newRole });
      toast.success("Role updated");
      load();
    } catch { toast.error("Failed to update role"); }
  };

  const resetPw = async (u) => {
    try {
      const res = await portalAdminResetPassword(u.id);
      setTempPw({ email: u.email, password: res.data.temp_password });
      toast.success("New temporary password generated");
    } catch { toast.error("Failed to reset password"); }
  };

  const resendWelcome = async (u) => {
    if (!window.confirm(`Resend the welcome email to ${u.email}? This generates a fresh temporary password.`)) return;
    try {
      const res = await portalAdminResetPassword(u.id);
      setTempPw({ email: u.email, password: res.data.temp_password });
      toast.success(`Welcome email resent to ${u.email}`);
    } catch { toast.error("Failed to resend welcome email"); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove portal access for ${u.email}?`)) return;
    try {
      await portalAdminDeleteUser(u.id);
      toast.success("Removed");
      load();
    } catch { toast.error("Failed to remove"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="portal-access-dialog">
        <DialogHeader><DialogTitle>Portal Access — {district}</DialogTitle></DialogHeader>

        <p className="text-sm text-muted-foreground">
          Give this district's reps a limited login. <strong>LEA</strong> can view attendance, assessments &amp; progress
          reports for this district's students. <strong>Medicaid Billing</strong> can view attendance and log DAT
          services. They only ever see this district's students.
        </p>

        <TempPasswordBanner email={tempPw?.email} password={tempPw?.password} onClose={() => setTempPw(null)} />

        {/* Create form */}
        <div className="rounded-lg border p-3 space-y-3 bg-slate-50">
          <p className="text-sm font-semibold">Add a login</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Contact Name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Jane Smith" data-testid="portal-access-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="jane@district.org" data-testid="portal-access-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Role</Label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="portal-access-role">
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="flex items-end">
              <Button onClick={create} disabled={creating} className="bg-[#00305E] hover:bg-[#00305E]/90 w-full" data-testid="portal-access-create">
                {creating ? "Creating…" : "Create Login"}
              </Button>
            </div>
          </div>
        </div>

        {/* Existing users */}
        <div>
          <p className="text-sm font-semibold mb-2">Existing logins</p>
          {loading ? (
            <p className="text-sm text-muted-foreground py-4">Loading…</p>
          ) : users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4" data-testid="portal-access-empty">No portal logins for this district yet.</p>
          ) : (
            <div className="space-y-2" data-testid="portal-access-list">
              {users.map((u) => (
                <div key={u.id} className="flex flex-wrap items-center gap-2 rounded-md border p-2 text-sm" data-testid={`portal-access-user-${u.id}`}>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium truncate">{u.name} <span className="text-muted-foreground font-normal">· {u.email}</span></p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <select value={u.role} onChange={(e) => changeRole(u, e.target.value)} className="h-7 rounded border border-input bg-background px-2 text-xs" data-testid={`portal-access-role-${u.id}`}>
                        {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
                      </select>
                      {u.active ? <Badge variant="outline" className="text-emerald-700 border-emerald-300">Active</Badge> : <Badge variant="outline" className="text-red-600 border-red-300">Disabled</Badge>}
                      {u.must_change_password && <Badge variant="outline" className="text-amber-700 border-amber-300">Pending first login</Badge>}
                    </div>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-[#00305E]" onClick={() => resendWelcome(u)} data-testid={`portal-access-resend-${u.id}`}>Resend Welcome</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => resetPw(u)} data-testid={`portal-access-reset-${u.id}`}>Reset PW</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => toggleActive(u)} data-testid={`portal-access-toggle-${u.id}`}>{u.active ? "Disable" : "Enable"}</Button>
                    <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-red-600 hover:bg-red-50" onClick={() => remove(u)} data-testid={`portal-access-delete-${u.id}`}>Remove</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
